import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useScryfallPrintings } from '@/hooks/useScryfall';
import { ScryfallCard } from '@/types/scryfall';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { useState } from 'react';

interface PrintingSelectorProps {
  card: ScryfallCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (oldCardId: string, newCard: ScryfallCard) => void;
}

export function PrintingSelector({ card, open, onOpenChange, onSelect }: PrintingSelectorProps) {
  const { data: printings, isLoading } = useScryfallPrintings(card.oracle_id, open);
  const [isFoil, setIsFoil] = useState(card.is_foil === 'foil');

  const handleSelect = (printing: ScryfallCard) => {
    onSelect(card.id, { ...printing, is_foil: isFoil ? 'foil' : '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Printing for {card.name}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center space-x-2">
          <Switch id="foil-switch" checked={isFoil} onCheckedChange={setIsFoil} />
          <Label htmlFor="foil-switch">Foil</Label>
        </div>
        <ScrollArea className="h-[60vh]">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
            {isLoading && Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[5/7] w-full" />
            ))}
            {printings?.map((printing) => (
              <div key={printing.id} className="relative group">
                <img
                  src={printing.image_uris?.normal || printing.card_faces?.[0]?.image_uris?.normal || ''}
                  alt={`${printing.name} (${printing.set})`}
                  className="w-full h-full object-cover rounded-lg"
                />
                {isFoil && (
                  <>
                    {/* Static rainbow holographic base */}
                    <div className="absolute inset-0 opacity-25 pointer-events-none"
                         style={{
                           background: 'linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #9400d3, #ff1493, #00ff7f, #1e90ff, #ff69b4, #ff0080)',
                           mixBlendMode: 'overlay'
                         }} />

                    {/* Static vibrant color layers */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-cyan-300/15 via-violet-400/20 to-fuchsia-300/15 opacity-25 pointer-events-none"
                         style={{ mixBlendMode: 'color-dodge' }} />
                  </>
                )}
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-center text-sm p-2">{printing.set_name}</p>
                  <Button size="sm" onClick={() => handleSelect(printing)}>Select</Button>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
